import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { drugRecallService } from '../services/drugRecallService'
import { DrugRecall } from '../types/drugRecall'
import { format } from 'date-fns'
import './DrugRecallDetail.css'

export function DrugRecallDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const isAffectedPatientsView = location.pathname.includes('/affected-patients')

  const { data: recall, isLoading, error } = useQuery<DrugRecall>({
    queryKey: ['drug-recall', id],
    queryFn: () => drugRecallService.getById(id!),
    enabled: !!id,
  })

  const { data: affectedPatients } = useQuery({
    queryKey: ['drug-recall-affected-patients', id],
    queryFn: () => drugRecallService.getAffectedPatients(id!),
    enabled: !!id,
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => drugRecallService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drug-recalls'] })
      navigate('/settings/drug-recalls')
    },
  })

  if (isLoading) return <div className="drug-recall-detail-loading">Loading recall details...</div>
  if (error) return <div className="drug-recall-detail-error">Error loading recall</div>
  if (!recall) return <div className="drug-recall-detail-not-found">Recall not found</div>

  // Show affected patients view if on that route
  if (isAffectedPatientsView) {
    return (
      <div className="drug-recall-detail-page">
        <div className="page-header">
          <div>
            <button className="btn-back" onClick={() => navigate(`/settings/drug-recalls/${id}`)}>
              ← Back to Recall
            </button>
            <h1>Affected Patients - {recall.title}</h1>
          </div>
        </div>
        <div className="affected-patients-page">
          {affectedPatients && affectedPatients.length > 0 ? (
            <div className="affected-patients-table">
              <table>
                <thead>
                  <tr>
                    <th>Patient Name</th>
                    <th>Medication</th>
                    <th>Batch/Lot</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {affectedPatients.map((patient) => (
                    <tr key={patient.patientId}>
                      <td>
                        <button
                          className="link-button"
                          onClick={() => navigate(`/patients/${patient.patientId}`)}
                        >
                          {patient.patientName}
                        </button>
                      </td>
                      <td>{patient.medicationName}</td>
                      <td>
                        {patient.batchNumber && <span>Batch: {patient.batchNumber}</span>}
                        {patient.lotNumber && <span>Lot: {patient.lotNumber}</span>}
                      </td>
                      <td>
                        {patient.notified ? (
                          <span className="status-badge status-notified">Notified</span>
                        ) : (
                          <span className="status-badge status-pending">Pending</span>
                        )}
                      </td>
                      <td>
                        <button
                          className="btn-secondary btn-sm"
                          onClick={() => navigate(`/patients/${patient.patientId}`)}
                        >
                          View Patient
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <p>No affected patients found for this recall</p>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="drug-recall-detail-page">
      <div className="page-header">
        <div>
          <button className="btn-back" onClick={() => navigate('/settings/drug-recalls')}>
            ← Back
          </button>
          <h1>{recall.title}</h1>
        </div>
        <div className="header-actions">
          <button
            className="btn-secondary"
            onClick={() => navigate(`/settings/drug-recalls/${id}/affected-patients`)}
          >
            View Affected Patients ({affectedPatients?.length || 0})
          </button>
          <button
            className="btn-danger"
            onClick={() => {
              if (confirm('Are you sure you want to delete this recall?')) {
                deleteMutation.mutate(recall.id)
              }
            }}
            disabled={deleteMutation.isPending}
          >
            Delete
          </button>
        </div>
      </div>

      <div className="recall-detail-content">
        <div className="recall-info-card">
          <div className="info-row">
            <span className="info-label">Drug:</span>
            <span className="info-value">{recall.drugName || recall.drugId}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Recall Date:</span>
            <span className="info-value">{format(new Date(recall.recallDate), 'MMM dd, yyyy')}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Severity:</span>
            <span className={`severity-badge severity-${recall.severity}`}>{recall.severity}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Status:</span>
            <span className={`status-badge status-${recall.status}`}>{recall.status}</span>
          </div>
          {recall.source && (
            <div className="info-row">
              <span className="info-label">Source:</span>
              <span className="info-value">{recall.source}</span>
            </div>
          )}
        </div>

        <div className="recall-section">
          <h3>Description</h3>
          <p>{recall.description}</p>
        </div>

        <div className="recall-section">
          <h3>Reason</h3>
          <p>{recall.reason}</p>
        </div>

        <div className="recall-section">
          <h3>Action Required</h3>
          <p>{recall.actionRequired}</p>
        </div>

        {recall.contactInformation && (
          <div className="recall-section">
            <h3>Contact Information</h3>
            <p>{recall.contactInformation}</p>
          </div>
        )}

        {recall.notes && (
          <div className="recall-section">
            <h3>Notes</h3>
            <p>{recall.notes}</p>
          </div>
        )}

        {affectedPatients && affectedPatients.length > 0 && (
          <div className="recall-section">
            <h3>Affected Patients ({affectedPatients.length})</h3>
            <div className="affected-patients-list">
              {affectedPatients.map((patient) => (
                <div key={patient.patientId} className="affected-patient-item">
                  <div className="patient-name">{patient.patientName}</div>
                  <div className="patient-medication">{patient.medicationName}</div>
                  {patient.notified && (
                    <div className="patient-notified">✓ Notified</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
